<script setup lang="ts">
import { CategoryOption, SubjectSummary, AlignedGoals, SelectedNeedsData } from "@/presentlevels/presentlevels_types.ts";
import type { ReportTypeMetadata } from "@/presentlevels/api_v2.ts";
import { Standard } from '@/standards/standard_types';
import { PropType, Ref, ref, watch, onBeforeUnmount, computed, onMounted, nextTick, provide, ComputedRef } from 'vue'
import PresentlevelsSkillsModal from "@/presentlevels/PresentlevelsSkillsModal.vue";
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store'
import WikiTextEditor from "@/components/editor/WikiTextEditor.vue";
import SaveToLibrary from '@/library/SaveToLibrary.vue'
import GbButton from '@/components/GbButton.vue'
import { transformResourceComponentsToWiki, transformWikiToResourceComponents } from '@/components/editor/wiki_tiptap_helpers'
import { getEditorPlainText, processStatementText } from './copy-statement-helpers'
import type { ImpactStatement } from './presentlevels_types'
import SubjectSummaryLoading from '@/presentlevels/SubjectSummaryLoading.vue'
import GbDialog from '@/components/GbDialog.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { PRESENTLEVELS_EVENT_NAMES } from '@/presentlevels/event_names.ts'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import StandardPopover from '@/standards/StandardPopover.vue'
import { headingsForStandardSetCode } from '@/standards/standard_headings.ts'
import { useLibraryStore } from '@/library/library.store.ts'
import { LibraryFolder } from '@/library/library_types.ts'
import TextLinkButton from '@/components/TextLinkButton.vue'
import CopySummaryModal from '@/presentlevels/CopySummaryModal.vue'
import { isFolderShared } from '@/library/library_helpers.ts'
import FolderIcon from '@/library/FolderIcon.vue'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable.js'
import { toolkitUrls } from '@/util/links.js'
import { useCopyText } from '@/util/use_copy_text'
import AssessmentUploadModal from '@/presentlevels/AssessmentUploadModal.vue'
import DropdownButton from '@/components/DropdownButton.vue'
import PresentlevelsImpactStatement from '@/presentlevels/PresentlevelsImpactStatement.vue'
import PresentlevelsEffectStatement from '@/presentlevels/PresentlevelsEffectStatement.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useContentReviewStore } from '@/content-review/content-review.store'
import { useContentReview } from '@/content-review/composables/useContentReview'
import { reviewPresentLevelsWithImpactStream } from '@/content-review/api'
import { usePresentLevelsReviewAnalytics } from '@/presentlevels/composables/usePresentLevelsReviewAnalytics'
import ContentReviewButton from '@/content-review/components/ContentReviewButton.vue'
import ContentReviewModal from '@/content-review/components/ContentReviewModal.vue'
import ContentReviewResults from '@/content-review/components/ContentReviewResults.vue'

const presentLevelsStore = usePresentLevelsStore()
const libraryStore = useLibraryStore()
const contentProfileStore = useContentProfileStore()
const visibilityStore = useVisibilityStore()
const contentReviewStore = useContentReviewStore()

// Get grade order for aligned goals API
const gradeOrder = computed(() => {
  const gradeUUID = props.summary?.grade_uuid
  if (!gradeUUID) return 0
  
  const gradeOption = presentLevelsStore.levelOptions.find(level => level.uuid === gradeUUID)
  return gradeOption?.order ?? 0
})

provide('subjectLabel', computed(() => props.summary?.subject_label || ''))
const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki
const standards = ref([] as Standard[])

const groupedStandards = computed(() => {
  const groups: { [key: string]: Standard[] } = {}
  for (const standard of standards.value) {
    const setCode = standard.standard_set_code
    if (!groups[setCode]) {
      groups[setCode] = []
    }
    groups[setCode].push(standard)
  }
  return groups
})

const emit = defineEmits<{
  'text-changed': [text: string]
  'impact-statements-changed': [statements: ImpactStatement[]]
  'effect-statement-changed': [effectStatement: string]
  'effect-statement-needs-update-changed': [needsUpdate: boolean]
  'copied': []
  'collapsed': [isCollapsed: boolean]
}>()

const props = defineProps({
  summary: {
    type: Object as PropType<SubjectSummary>,
    required: true
  },
  categoryOptions: {
    type: Array as PropType<Array<CategoryOption>>,
    default: () => []
  },
  savedSummary: {
    type: Boolean,
    default: false
  },
})

const analyticsTracker = getAnalyticsTracker().getChildContext({
  plw_statement_subject: props.summary?.subject_label
})

const placeHolder = computed(() => {
  // For saved summaries without AI features, show read-only placeholder
  if (props.savedSummary && !visibilityStore.aiFeatureVisibility) {
    return 'Saved statement (AI features disabled)'
  }
  if (reportUploadOptions.value.length > 0 && visibilityStore.reportUploadVisibility) {
    return 'Upload a score report or select a category below to begin drafting statement'
  }
  return 'Select a category below to begin drafting statement'
})
const statementText = ref(props.summary?.statement_text)

// Initialize impact statements from props
const impactStatements = ref<ImpactStatement[]>(props.summary?.impact_statements || [])
const isSyncingFromProps = ref(false)

// Content Review setup
const showReviewModal = ref(false)
const hasDismissedPresentLevelsResults = ref(false)
const hasDismissedImpactResults = ref(false)
const { isReviewing, reviewResults, reviewError, startReview, cancelReview, clearResults } = useContentReview()

const presentLevelsCriteria = computed(() =>
  contentReviewStore.presentLevelsCriteriaForProfile(
    contentProfileStore.activeProfile?.id || '',
    visibilityStore.betaVisibility
  )
)

const impactCriteria = computed(() =>
  contentReviewStore.impactCriteriaForProfile(
    contentProfileStore.activeProfile?.id || '',
    visibilityStore.betaVisibility
  )
)

const reviewCriteriaByType = computed((): Record<string, typeof presentLevelsCriteria.value> => {
  const result: Record<string, typeof presentLevelsCriteria.value> = {}
  if (presentLevelsCriteria.value.length > 0) result.presentlevels = presentLevelsCriteria.value
  if (impactCriteria.value.length > 0) result.impact = impactCriteria.value
  return result
})

// Only show custom criteria set name if a custom profile is active AND has visible criteria.
// Uses the store's criteriaSetNameForContentType getter which returns custom name
// only if custom profile has visible criteria (otherwise returns default name).
const reviewCriteriaSetName = computed(() => {
  const profileId = contentProfileStore.activeProfile?.id || ''
  const betaMode = visibilityStore.betaVisibility

  // Early return if no custom profile is active (avoid treating default as custom)
  if (!contentReviewStore.isCustomCriteriaSet(profileId, betaMode)) {
    return ''
  }

  // Get the effective name for each content type (returns custom name if custom has visible criteria)
  const presentLevelsName = contentReviewStore.criteriaSetNameForContentType(profileId, 'presentlevels', betaMode)
  const impactName = contentReviewStore.criteriaSetNameForContentType(profileId, 'impact', betaMode)
  const customName = contentReviewStore.criteriaSetName(profileId, betaMode)

  // If either content type uses custom criteria, return the custom name
  if (presentLevelsName === customName || impactName === customName) {
    return customName
  }

  return ''
})

const showReviewButton = computed(() =>
  Object.keys(reviewCriteriaByType.value).length > 0
)

const hasReviewResults = computed(() => reviewResults.value.length > 0)

const presentLevelsResults = computed(() =>
  reviewResults.value.filter((r) => r.section === 'presentlevels')
)

const impactResults = computed(() =>
  reviewResults.value.filter((r) => r.section === 'impact')
)

const criteriaEventAttrs = computed(() => ({
  criteria_label: reviewCriteriaSetName.value || 'default',
  is_custom_criteria_set: contentReviewStore.isCustomCriteriaSet(
    contentProfileStore.activeProfile?.id || '',
    visibilityStore.betaVisibility
  ),
}))

const {
  handleAutoReviewButtonClick,
  handleModalReview,
  handleCloseReviewResults,
  handleReviewResultsToggle,
  handleReceivedReviewResults,
} = usePresentLevelsReviewAnalytics(analyticsTracker, criteriaEventAttrs, hasReviewResults)

function handleReview() {
  hasDismissedPresentLevelsResults.value = false
  hasDismissedImpactResults.value = false
  handleModalReview(() => {
    showReviewModal.value = false
    const combinedImpactText = impactStatements.value
      .map((s) => s.statement_text)
      .filter(Boolean)
      .join('\n\n')
    startReview(
      ({ signal, onProgress }) =>
        reviewPresentLevelsWithImpactStream({
          presentLevelsText: statementText.value || '',
          impactStatementText: combinedImpactText,
          contentProfile: contentProfileStore.activeProfile?.id || '',
          subjects: props.summary?.subject_label ? [props.summary.subject_label] : [],
          betaMode: visibilityStore.betaVisibility,
          signal,
          onProgress,
        }),
      (results) => {
        handleReceivedReviewResults(results)
      }
    )
  })
}

function handleClosePresentLevelsResults() {
  handleCloseReviewResults(() => {
    hasDismissedPresentLevelsResults.value = true
  })
}

function handleCloseImpactResults() {
  handleCloseReviewResults(() => {
    hasDismissedImpactResults.value = true
  })
}

function handleOpenReviewModal() {
  if (isCollapsed.value) {
    isCollapsed.value = false
    emit('collapsed', false)
  }
  handleAutoReviewButtonClick(() => {
    showReviewModal.value = true
  })
}

// Clear review results when statement changes significantly
watch(() => props.summary?.statement_text, () => {
  if (hasReviewResults.value) {
    clearResults()
    hasDismissedPresentLevelsResults.value = false
    hasDismissedImpactResults.value = false
  }
})

const saveStatus = computed(() => {
  if (!props.summary?.uuid) return PL_SUMMARY_SAVING_STATES.NO_CHANGES
  return presentLevelsStore.savingStates[props.summary.uuid] || PL_SUMMARY_SAVING_STATES.NO_CHANGES
})

const libraryFolder: ComputedRef<LibraryFolder | undefined> = computed(() => {
  const uuid = props.summary?.uuid
  return uuid ? libraryStore.getFolderByContentUuid(uuid) : undefined
})

const userRole = computed(() => {
  if (!libraryFolder.value) {
    return undefined
  }
  return libraryStore.getUserRoleForFolder(libraryFolder.value?.uuid)
})
const isContributor = computed(() => userRole.value === 'contributor')

const getStandards = (): void => {

  const standardsForSummary = props.summary?.standards ?? []
  if (!standardsForSummary.length) {
    return
  }
  const standardSetsForProfile: string[] = contentProfileStore.visibleStandardSets ?? []
  const relevantStandards: Standard[] = standardsForSummary.filter((s: Standard) => standardSetsForProfile.includes(s.standard_set_code))
  const allStandards = [...standards.value, ...relevantStandards];
  standards.value = Array.from(new Map(allStandards.map((s: Standard) => [s.standard_long_ref, s])).values());
}

onMounted(() => {
  getStandards()
  if (contentReviewStore.profiles.length === 0 && !contentReviewStore.loading) {
    contentReviewStore.fetchProfiles()
  }
})

watch(() => props.summary?.standards, () => {
  getStandards()
}, { deep: true })

watch(() => contentProfileStore.activeProfile, () => {
  standards.value = []
  getStandards()
})

watch(() => props.summary?.statement_text, (newStatementText) => {
  if (newStatementText !== statementText.value) {
    isSyncingFromProps.value = true
    statementText.value = newStatementText
    nextTick().then(() => {
      isSyncingFromProps.value = false
    })
  }
})

watch(() => props.summary?.impact_statements, (newImpactStatements) => {
  if (JSON.stringify(newImpactStatements) !== JSON.stringify(impactStatements.value)) {
    isSyncingFromProps.value = true
    impactStatements.value = newImpactStatements ? JSON.parse(JSON.stringify(newImpactStatements)) : []
    nextTick().then(() => {
      isSyncingFromProps.value = false
    })
  }
}, { deep: true })

watch(statementText, () => {
  // For saved summaries, always emit changes (even from props) so skills modal updates are tracked
  // For non-saved summaries, don't emit if syncing from props to avoid triggering saves during reset
  if (!isSyncingFromProps.value || props.savedSummary) {
    emit('text-changed', statementText.value || '')

    if (!props.savedSummary) {
      debouncedSaveStatementText()
    } else {
      // For saved summaries, check if we need to set the effect statement update flag
      const hasEffectStatement = !!props.summary?.effect_statement
      if (hasEffectStatement) {
        emit('effect-statement-needs-update-changed', true)
      }
    }
  }
})

watch(impactStatements, () => {
  // Clear review results when impact statements change
  if (hasReviewResults.value) {
    clearResults()
    hasDismissedPresentLevelsResults.value = false
    hasDismissedImpactResults.value = false
  }

  // For saved summaries, always emit changes (even from props) so skills modal updates are tracked
  // For non-saved summaries, don't emit if syncing from props to avoid infinite loop
  if (!isSyncingFromProps.value || props.savedSummary) {
    emit('impact-statements-changed', impactStatements.value)
    
    if (!props.savedSummary) {
      debouncedSaveStatementText()
    } else {
      // For saved summaries, check if we need to set the effect statement update flag
      const hasEffectStatement = !!props.summary?.effect_statement
      if (hasEffectStatement) {
        emit('effect-statement-needs-update-changed', true)
      }
    }
  }
}, { deep: true })

const saveStatementText = async (): Promise<void> => {
  if (!props.summary?.uuid) return

  const hasEffectStatement = !!props.summary?.effect_statement
  
  try {
    const fieldsToUpdate: any = {
      statement_text: statementText.value || '',
      aligned_goals: props.summary?.aligned_goals,
      impact_statements: impactStatements.value
    }
    
    // If there's an effect statement and the statement text has changed, mark it as needing update
    if (hasEffectStatement) {
      fieldsToUpdate.effect_statement_needs_update = true
    }
    
    await presentLevelsStore.saveSubjectSummary(
      props.summary.uuid,
      fieldsToUpdate
    )
    
    // In saved summary context, emit the change
    if (hasEffectStatement && props.savedSummary) {
      emit('effect-statement-needs-update-changed', true)
    }
  } catch (error) {
    console.error('Failed to save statement text:', error)
  }
}

const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
const statusResetTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
const debouncedSaveStatementText = () => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value)
  }

  timeoutId.value = setTimeout(() => {
    void saveStatementText()
  }, 300)
}

function discardTextEdits(statements: ImpactStatement[], text?: string) {
  isSyncingFromProps.value = true
  impactStatements.value = statements ? JSON.parse(JSON.stringify(statements)) : []
  if (text !== undefined) {
    statementText.value = text
  }
  nextTick().then(() => {
    isSyncingFromProps.value = false
  })
}

defineExpose({
  saveStatementText,
  discardTextEdits
})

function handleEditModeUpdated(newIsEditMode: boolean) {
  if (!props.savedSummary && !newIsEditMode) {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }
  }
}

const impactStatementCopyTitle = computed(() => {
  if (!impactStatements.value || impactStatements.value.length === 0) {
    return ''
  }
  
  // Build title from all impact statements: "Designation: Impact Area 1 | Impact Area 2"
  const titles = impactStatements.value.map(statement => {
    const areas = statement.impact_area_labels.join(' | ')
    return `${statement.designation_label}: ${areas}`
  })
  
  // Join multiple statements with semicolon
  return titles.join('; ')
})

const selectedCategory: Ref<CategoryOption | null> = ref(null)
function setSelectedCategory(category: CategoryOption | null) {
  selectedCategory.value = category

  if (category) {
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLICKED_CATEGORY, {plw_statement_category: category.label})
  }
}

async function deleteStatement() {
  if (props.summary?.uuid) {
    await presentLevelsStore.deleteStatement(props.summary.uuid)
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.DELETED_AREA)
  }
}
const showDeleteModal = ref(false)
const showCopySummaryModal = ref(false)
const clearModalType = ref<'strengths' | 'impact' | 'effect' | null>(null)
const impactStatementIndexToRemove = ref<number | null>(null)
const effectStatementRef = ref<InstanceType<typeof PresentlevelsEffectStatement> | null>(null)
const savedFolderError = ref(false)

const handleSaveToFolder = async () => {
  savedFolderError.value = false
  if (props.summary?.uuid) {
    try {
      await libraryStore.fetchFoldersForPLSubjectSummaries()
    } catch (error) {
      console.error('Failed to refresh library state after save:', error)
    }
  }
}
function handleSaveToFolderError(error: unknown) {
  savedFolderError.value = true
  console.error('Save to library failed:', error)
}

const editorWrapperClasses: ComputedRef<string> = computed(() => {
  const classes = ['rounded-md']
  if (isContributor.value) {
    classes.push('bg-gray-200')
    classes.push('py-1')
  } else if (props.summary?.subject_label === 'Reading') {
    classes.push('bg-blue-50')
    classes.push('overflow-hidden')
  } else {
    classes.push('bg-white')
    classes.push('py-2')
  }
  return classes.join(' ')
})

const readOnlyMessageClasses = 'py-2 px-1.5 bg-gray-1000 text-white font-default text-sm font-regular leading-[1.125rem] mt-1 flex items-center gap-1'

const isBehaviorSubject = computed(() => props.summary?.subject_label === 'Behavior')
const browseResourcesSubject = computed(() => {
  if (!isBehaviorSubject.value) return ''
  return getGoalSubjectLabelFromValue('Behavior & SEL')
})

const impactStatementSubject = computed(() => {
  const subject = props.summary?.subject_label || ''
  if (subject === 'Social & Emotional Learning' || subject === 'Social Skills') {
    return 'Behavior'
  }
  return subject
})

function handleBehaviorLinkClick() {
  analyticsTracker.sendEvent(PRESENTLEVELS_EVENT_NAMES.RESOURCES.BROWSE_BEHAVIOR_DATA_COLLECTION)
}

function handleAddImpactStatement(statement: ImpactStatement) {
  impactStatements.value.push(statement)
}

function handleRemoveImpactStatement(index: number) {
  impactStatementIndexToRemove.value = index
  clearModalType.value = 'impact'
}

function handleUpdateImpactStatement(index: number, statementText: string) {
  if (impactStatements.value[index]) {
    impactStatements.value[index].statement_text = statementText
  }
}

async function handleEffectStatementGenerated(generatedText: string) {
  console.log('Effect statement generated:', generatedText)
  emit('effect-statement-changed', generatedText)
  
  // Save the effect statement and clear the needs-update flag
  if (!props.savedSummary && props.summary?.uuid) {
    try {
      await presentLevelsStore.saveSubjectSummary(
        props.summary.uuid,
        { 
          effect_statement: generatedText,
          effect_statement_needs_update: false 
        }
      )
    } catch (error) {
      console.error('Failed to save effect statement:', error)
    }
  }
}

function handleEffectStatementError(error: Error) {
  console.error('Effect statement generation error:', error)
}

function handleEffectStatementClearRequested() {
  clearModalType.value = 'effect'
}

async function handleEffectStatementNeedsUpdateChanged(needsUpdate: boolean) {
  emit('effect-statement-needs-update-changed', needsUpdate)
  
  // In non-saved summary context, save the flag to the backend immediately
  if (!props.savedSummary && props.summary?.uuid) {
    try {
      await presentLevelsStore.saveSubjectSummary(
        props.summary.uuid,
        { effect_statement_needs_update: needsUpdate }
      )
    } catch (error) {
      console.error('Failed to update effect statement needs update flag:', error)
    }
  }
}

const isCollapsed = ref(false)
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
  emit('collapsed', isCollapsed.value)
}

const { copyText, isTooltipOpen } = useCopyText()
const { copyText: copyAllText, isTooltipOpen: isAllStatementsTooltipOpen } = useCopyText()
const wikiTextEditorRef = ref<InstanceType<typeof WikiTextEditor> | null>(null)

const showAssessmentModal = ref(false)
const selectedReportMetadata = ref<ReportTypeMetadata | null>(null)

const UPLOAD_ICON = 'file_upload'

const reportUploadOptions = computed(() => {
  const subjectLabel = props.summary?.subject_label
  if (!subjectLabel) return []
  
  const optionsForSubject = presentLevelsStore.reportUploadOptions[subjectLabel] || []
  
  return optionsForSubject.map((reportMetadata) => {
    return {
      label: reportMetadata.dropdown_title,
      subtitle: subjectLabel,
      icon: UPLOAD_ICON,
      command: () => handleUploadScoreReportClick(reportMetadata)
    }
  })
})

const reportUploadOptionsText = computed(() => {
  const subjectLabel = props.summary?.subject_label
  if (!subjectLabel) return ''
  
  const optionsForSubject = presentLevelsStore.reportUploadOptions[subjectLabel] || []
  
  if (optionsForSubject.length === 0) return ''
  
  const names = optionsForSubject.map(opt => opt.name)
  
  // Format the list: "A, B, & C" or "A & B" or just "A"
  if (names.length === 1) {
    return names[0]
  } else if (names.length === 2) {
    return `${names[0]} & ${names[1]}`
  } else {
    const allButLast = names.slice(0, -1).join(', ')
    const last = names[names.length - 1]
    return `${allButLast}, & ${last}`
  }
})

function handleUploadScoreReportClick(reportMetadata: ReportTypeMetadata) {
  analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.CLICKED_UPLOAD, {
    score_report_series: reportMetadata.name,
    score_report_title: reportMetadata.upload_modal_title
  })
  selectedReportMetadata.value = reportMetadata
  showAssessmentModal.value = true
}

async function handleStatementInserted(text: string, alignedGoals: AlignedGoals | null) {
  if (!props.summary?.uuid) return
  
  try {
    // Append the new statement to the existing statement text
    const existingText = statementText.value || ''
    const combinedText = existingText ? `${existingText}<p></p>${text}` : text
    
    // Merge aligned goals with existing goals if provided
    let goalsToSave = props.summary?.aligned_goals
    if (alignedGoals) {
      const existingGoals = props.summary?.aligned_goals || []
      goalsToSave = [...existingGoals, ...alignedGoals]
    }
    
    // Save statement and aligned goals (if already fetched)
    await presentLevelsStore.saveSubjectSummary(
      props.summary.uuid,
      {
        statement_text: combinedText,
        aligned_goals: goalsToSave,
        impact_statements: impactStatements.value
      }
    )
  } catch (error) {
    console.error('Failed to save statement from assessment upload:', error)
  }
}

async function handleAlignedGoalsFetched(summaryUUID: string, goals: AlignedGoals) {
  // This is called when aligned goals finish fetching after the modal has closed
  if (summaryUUID !== props.summary?.uuid) return
  
  try {
    // Merge with existing goals
    const existingGoals = props.summary?.aligned_goals || []
    const mergedGoals = [...existingGoals, ...goals]
    
    await presentLevelsStore.saveSubjectSummary(
      summaryUUID,
      { aligned_goals: mergedGoals }
    )
    
    // Clear loading state in store (in case it wasn't cleared yet)
    presentLevelsStore.loadingAlignedGoals[summaryUUID] = false
  } catch (error) {
    console.error('Failed to save aligned goals that arrived after insertion:', error)
    // Clear loading state even on error
    presentLevelsStore.loadingAlignedGoals[summaryUUID] = false
  }
}

async function copyAllStatements(event: MouseEvent) {
  const statements: string[] = []
  
  // Get strengths/needs statement text
  // Use the underlying data instead of editor instance to work when collapsed
  if (statementText.value) {
    const processedText = processStatementText(statementText.value)
    if (processedText.trim()) {
      statements.push(processedText)
    }
  }
  
  // Get all impact statements
  if (impactStatements.value && impactStatements.value.length > 0) {
    impactStatements.value.forEach(statement => {
      const processedText = processStatementText(statement.statement_text)
      if (processedText.trim()) {
        statements.push(processedText)
      }
    })
  }
  
  if (statements.length > 0) {
    const combinedText = statements.join('\n\n')
    try {
      await copyAllText(combinedText, event, { autoHideTooltip: true, autoHideDelay: 3000 })
    } catch (error) {
      console.error('Failed to copy all statements:', error)
    }
  }
}

async function copyStatementText(event: MouseEvent) {
  const processedText = getEditorPlainText(wikiTextEditorRef.value)
  if (processedText) {
    try {
      await copyText(processedText, event, { autoHideTooltip: true, autoHideDelay: 3000 })
    } catch (error) {
      console.error('Failed to copy statement text:', error)
      // Show error message via tooltip by setting isTooltipOpen
      // The tooltip will show the error state briefly
      isTooltipOpen.value = true
      setTimeout(() => {
        isTooltipOpen.value = false
      }, 3000)
    }
  }
}

function clearStrengthsNeeds() {
  clearModalType.value = 'strengths'
}

function confirmClear() {
  if (clearModalType.value === 'strengths') {
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLEARED_STATEMENT, { statement_type: 'strength_need_statement' })
    statementText.value = ''
  } else if (clearModalType.value === 'impact' && impactStatementIndexToRemove.value !== null) {
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLEARED_STATEMENT, { statement_type: 'impact_statement' })
    impactStatements.value.splice(impactStatementIndexToRemove.value, 1)
    impactStatementIndexToRemove.value = null
  } else if (clearModalType.value === 'effect') {
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLEARED_STATEMENT, { statement_type: 'effect_statement' })
    effectStatementRef.value?.clearStatement()
  }
  clearModalType.value = null
}

const clearModalConfig = computed(() => {
  if (clearModalType.value === 'strengths') {
    return {
      header: 'Clear Strengths & Needs Statement?',
      body: `You have created a <span class="font-bold">Strengths & Needs Statement in ${props.summary?.subject_label}</span>. If you proceed, all content in this statement will be lost. Are you sure you want to clear this statement?`
    }
  } else if (clearModalType.value === 'impact') {
    return {
      header: 'Clear Impact Statement?',
      body: `You have created an <span class="font-bold">Impact Statement in ${props.summary?.subject_label}</span>. If you proceed, all content in this statement will be lost. Are you sure you want to clear this statement?`
    }
  } else if (clearModalType.value === 'effect') {
    return {
      header: 'Clear Effect Statement?',
      body: `You have created an <span class="font-bold">Effect Statement in ${props.summary?.subject_label}</span>. If you proceed, all content in this statement will be lost. Are you sure you want to clear this statement?`
    }
  }
  return { header: '', body: '' }
})

onBeforeUnmount(() => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value)
  }
  if (statusResetTimeoutId.value) {
    clearTimeout(statusResetTimeoutId.value)
  }
  cancelReview()
})
</script>

<template>
  <div class="md:w-plw-subject-summary rounded-lg border border-[#CCC] shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1),0_10px_15px_-3px_rgba(0,0,0,0.1)] overflow-hidden" :class="isCollapsed ? 'max-h-[60px] mb-2' : 'mb-3'">
    <div v-if="props.categoryOptions?.length">
      <div 
        role="button"
        tabindex="0"
        :aria-expanded="!isCollapsed"
        :aria-controls="`subject-summary-content-${props.summary?.uuid}`"
        class="bg-blue-200 hover:bg-[#CEF] px-5 py-3 flex items-center justify-between cursor-pointer transition-colors"
        @click="toggleCollapse"
        @keydown.enter="toggleCollapse"
        @keydown.space.prevent="toggleCollapse"
      >
          <div class="flex items-center gap-2">
            <span 
              class="material-icons-round text-2xl transition-transform duration-200"
              :class="{ '-rotate-90': isCollapsed }"
            >
              expand_more
            </span>
            <span class="text-xl font-bold truncate max-w-[240px]" :title="props.summary?.subject_label?.toUpperCase()">{{ props.summary?.subject_label?.toUpperCase() }}</span>
            <CopyTrackWrapper
              type="presentlevelsubjectsummary"
            >
              <div class="relative">
                <TooltipProvider :delayDuration="0">
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <button
                        @click.stop="copyAllStatements"
                        type="button"
                        class="flex items-center justify-center p-2.5 hover:bg-gray-200 rounded-full transition-colors min-w-[44px] min-h-[44px]"
                        aria-label="Copy all statements"
                      >
                        <span class="material-icons-outlined text-xl">content_copy</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" v-if="!isAllStatementsTooltipOpen">
                      <p>Copy {{ props.summary?.subject_label }} statements</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider :delayDuration="0">
                  <Tooltip :open="isAllStatementsTooltipOpen">
                    <TooltipTrigger as-child>
                      <div class="absolute inset-0 pointer-events-none"></div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Statements copied!</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CopyTrackWrapper>
            <a
              v-if="isBehaviorSubject"
              :href="`${toolkitUrls.browseAssessments}?subject=${encodeURIComponent(browseResourcesSubject)}&category=Behavior%20Data%20Collection`"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-400 hover:text-blue-500 text-md font-normal leading-4"
              @click.stop="handleBehaviorLinkClick"
            >
              <span class="inline-flex items-baseline gap-0.5"><span class="hover:underline">Browse data collection resources</span><span class="material-icons text-sm translate-y-0.5">open_in_new</span></span>
            </a>
          </div>
          <div class="flex items-center gap-3">
            <ContentReviewButton
              :disabled="!statementText && !impactStatements.some(s => s.statement_text)"
              :extra-classes="'!w-auto justify-self-end'"
              v-if="showReviewButton"
              :criteria-set-name="reviewCriteriaSetName"
              :has-existing-results="hasReviewResults"
              :show-criteria-set-name="false"
              @click="handleOpenReviewModal"
            />
            <div v-if="!savedSummary && !isContributor" class="flex items-center gap-4" @click.stop>
              <div v-if="libraryFolder?.name" class="text-md flex items-center gap-1 text-green-300" id="saved-to-library-label">
                <span>Saved to <router-link
                  id="saved-folder-link"
                  :to="{ name: 'LibraryFolder', params: { uuid: libraryFolder.uuid }, query: { ref: 'plw_folder_link' } }"
                  target="_blank"
                  rel="noopener"
                  :aria-label="`Open ${libraryFolder.name} folder in a new tab`"
                  class="border-b border-green-300 items-center hover:text-green-400 transition-colors"
                >
                  <FolderIcon :isShared="!!libraryFolder && isFolderShared(libraryFolder)" color-class="text-green-300" />
                  <span class="fs-exclude">{{ libraryFolder.name }}</span>
                  <span class="sr-only">(opens in a new tab)</span>
                </router-link></span>
              </div>
              <template v-else>
                <SaveToLibrary
                  :content-uuid="props.summary?.uuid"
                  content-type="pl_student_summary"
                  content-type-label="Student Summary"
                  size="xs"
                  class="[&_button]:!px-[63px] [&_button]:!py-2"
                  @save="handleSaveToFolder"
                  @saveerror="handleSaveToFolderError"
                >
                  <template #icon>
                    <span class="material-icons-round text-lg">folder</span>
                  </template>
                  <template #text>
                    Save
                  </template>
                </SaveToLibrary>
                <div v-if="savedFolderError" class="text-md flex items-center text-red-300" id="save-error-label"><span class="material-icons-round">warning_amber</span><span>Save failed.</span></div>
              </template>
              <TooltipProvider :delayDuration="0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      class="flex items-center justify-center p-2.5 rounded-full hover:bg-gray-200 transition-colors min-w-[44px] min-h-[44px]"
                      @click="showDeleteModal = true"
                      aria-label="Delete statement"
                    >
                      <span class="material-icons-outlined text-[16px]">delete_outline</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent class="max-w-[300px]">
                    <p>Delete statement</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
      </div>

      <div 
        v-if="!isCollapsed" 
        :id="`subject-summary-content-${props.summary?.uuid}`"
        class="bg-white"
      >
          <template v-if="isContributor">
            <p v-if="savedSummary" :class="readOnlyMessageClasses">
              <span class="material-icons text-lg" aria-hidden="true">lock</span>
              To make edits, copy this statement into a folder in your own library.
            </p>
            <div v-else :class="`${readOnlyMessageClasses} flex flex-nowrap items-center gap-1`">
              <span class="material-icons text-lg">lock</span>
              Only the owner of this Shared Folder can make edits. To make edits,
              <TextLinkButton
                text="copy to your Library"
                text-size="sm"
                icon-name="content_copy"
                icon-style="outlined"
                color="white"
                :default-underlined="true"
                @click="showCopySummaryModal = true"
              />
            </div>
          </template>

          <CopyTrackWrapper type="strength_need_statement">
            <div class="px-5 pt-3 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-md font-bold">Strengths & Needs</span>
                <div v-if="statementText" class="relative">
                  <TooltipProvider :delayDuration="0">
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <button
                          @click="copyStatementText"
                          class="flex items-center justify-center p-1 hover:bg-gray-200 rounded-full transition-colors"
                          aria-label="Copy statement text"
                        >
                          <span class="material-icons-outlined text-xl">content_copy</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" v-if="!isTooltipOpen">
                        <p>Copy strengths & needs statement</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider :delayDuration="0">
                    <Tooltip :open="isTooltipOpen">
                      <TooltipTrigger as-child>
                        <div class="absolute inset-0 pointer-events-none"></div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>Statement copied!</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <TooltipProvider v-if="statementText" :delayDuration="0">
                <Tooltip>
                  <TooltipTrigger as-child>
                    <button
                      @click="clearStrengthsNeeds"
                      class="text-blue-400 hover:text-blue-500 text-sm font-normal transition-colors"
                      type="button"
                    >
                      Clear
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>Clear strengths & needs statement</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <ContentReviewResults
              v-if="!hasDismissedPresentLevelsResults && (isReviewing || reviewError || presentLevelsResults.length > 0)"
              class="m-5"
              title="Strength & Needs Review"
              :results="presentLevelsResults"
              :is-loading="isReviewing"
              :error="reviewError"
              :expected-count="presentLevelsCriteria.length"
              loading-text="Reviewing strengths and needs..."
              @toggle="handleReviewResultsToggle"
              @close="handleClosePresentLevelsResults"
            />

            <WikiTextEditor
              ref="wikiTextEditorRef"
              v-model:wikiText="statementText"
              :toEmbeddedText="toEmbeddedText"
              :toWikiText="toWikiText"
              :outer-class="props.summary?.subject_label === 'Reading' ? 'px-5 pb-3 reading-plw-editor' : 'px-5 pb-3'"
              :editor-wrapper-class="editorWrapperClasses"
              :placeHolder="placeHolder"
              :reset-value="props.summary?.statement_text"
              edited-text-type="strength_need_statement"
              :read-only="isContributor"
              :hide-controls="true"
              @editModeUpdated="handleEditModeUpdated"
            >
              <template v-if="!isContributor" #innerContent>
                <!-- Upload Score Report footer -->
                <div
                  v-if="reportUploadOptions.length > 0 && visibilityStore.reportUploadVisibility && !(props.savedSummary && !visibilityStore.aiFeatureVisibility)"
                  class="flex items-center gap-3 px-3 py-3 border-t border-[#b8d4e8] bg-[#d6eaf5] rounded-b-lg mx-0.5"
                >
                  <DropdownButton
                    dropdown-id="upload-assessment-menu"
                    button-theme="grayOutlineSecondary"
                    button-aria-label="Upload Score Report"
                    button-size="small"
                    additional-classes="!rounded-full !w-[44px] !h-[44px] !p-0 flex-shrink-0 !bg-white !flex !items-center !justify-center hover:!bg-gray-300"
                    menu-content-classes="!min-w-[240px]"
                    menu-label="Upload Score Report"
                    :items="reportUploadOptions"
                  >
                    <template #buttonContents>
                      <span class="material-icons-outlined text-[24px] text-[#2c5f7a]">add</span>
                    </template>
                  </DropdownButton>
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold text-blue-900 text-lg">Upload Score Report</span>
                    </div>
                    <p class="text-md text-blue-800 mt-0.5">
                      {{ reportUploadOptionsText }} · More assessment support coming soon
                    </p>
                  </div>
                </div>
                <!-- Standards (all subjects) -->
                <div v-if="Object.keys(groupedStandards).length" data-testid="inner-bottom-content" class="text-sm my-1">
                  <div v-for="(standardGroup, setCode) in groupedStandards" :key="setCode">
                    {{ headingsForStandardSetCode(setCode as string, { profileId: contentProfileStore.activeProfile?.id }) || standardGroup[0].standard_set }}:
                    <span v-for="standard in standardGroup" :key="standard.standard_long_ref">
                      <StandardPopover :standard="standard" /><span v-if="standard !== standardGroup[standardGroup.length - 1]">, </span>
                    </span>
                  </div>
                </div>
                <!-- Category chips (all subjects) -->
                <div v-if="!(props.savedSummary && !visibilityStore.aiFeatureVisibility)" class="flex flex-wrap gap-x-2 gap-y-2 mt-2">
                  <GbButton
                    v-for="categoryOption in categoryOptions"
                    :key="categoryOption.uuid"
                    :id="`category-button-${categoryOption.uuid}`"
                    :label="categoryOption.label"
                    gbTheme="grayOutlineSecondary"
                    size="small"
                    @click="setSelectedCategory(categoryOption)">
                    <span class="material-icons-round mr-1 text-green-300 text-base">add_circle</span>{{ categoryOption.label }}
                  </GbButton>
                </div>
              </template>
              <template #innerBottomContent>
              </template>
              <template #bottomLeftContent>
                <span v-if="saveStatus === PL_SUMMARY_SAVING_STATES.SAVING" class="material-icons-round text-lg animate-spin">autorenew</span>
              </template>
              <template #bottomRightContent>
              </template>
            </WikiTextEditor>
          </CopyTrackWrapper>

          <div v-if="visibilityStore.effectsVisibility && ['Reading', 'Writing', 'Math', 'Alt Academics: Reading', 'Alt Academics: Writing', 'Alt Academics: Math'].includes(props.summary?.subject_label || '')" class="px-5 py-[18px] border-t border-gray-300">
            <PresentlevelsEffectStatement
              ref="effectStatementRef"
              :summary-uuid="props.summary?.uuid || ''"
              :statement-text="statementText || ''"
              :save-to-summary="!props.savedSummary"
              :initial-effect-statement="props.summary?.effect_statement"
              :effect-statement-needs-update="props.summary?.effect_statement_needs_update"
              :subject-label="props.summary?.subject_label"
              @generated="handleEffectStatementGenerated"
              @error="handleEffectStatementError"
              @update-flag="handleEffectStatementNeedsUpdateChanged"
              @clear-requested="handleEffectStatementClearRequested"
            />
          </div>

          <div v-else-if="['Reading', 'Writing', 'Math', 'Behavior', 'Social & Emotional Learning', 'Social Skills'].includes(props.summary?.subject_label || '') && !(props.savedSummary && !visibilityStore.aiFeatureVisibility)" class="px-5 py-[18px] border-t border-gray-300">
            <CopyTrackWrapper
              type="impact_statement"
              :title="impactStatementCopyTitle"
            >
              <PresentlevelsImpactStatement
                :subject-label="impactStatementSubject"
                :display-subject-label="props.summary?.subject_label || ''"
                :student-name="props.summary?.student_name || ''"
                :impact-statements="impactStatements"
                :review-results="!hasDismissedImpactResults ? impactResults : []"
                :is-reviewing="!hasDismissedImpactResults && isReviewing"
                :review-error="!hasDismissedImpactResults ? reviewError : null"
                :expected-review-count="impactCriteria.length"
                @add="handleAddImpactStatement"
                @remove="handleRemoveImpactStatement"
                @update="handleUpdateImpactStatement"
                @close-review="handleCloseImpactResults"
                @toggle-review="handleReviewResultsToggle"
              />
            </CopyTrackWrapper>
          </div>
        </div>
    </div>
    <SubjectSummaryLoading v-else />

    <GbDialog
      dialog-id="delete-statement-modal"
      :visible="showDeleteModal"
      @update:visible="showDeleteModal = $event"
      header="Delete Statement"
      :footer-buttons="true"
    >
      <template #bodyContent>
        <p>You have created content for this <span class="font-bold">{{ props.summary?.subject_label?.toUpperCase() }}</span> statement. If you proceed, all content in this statement will be lost. Are you sure you want to delete this statement?</p>
      </template>
      <template #footerButtonLeft>
        <GbButton
          type="button"
          gbTheme="grayOutlineSecondary"
          label="Cancel"
          @click="showDeleteModal = false">
        </GbButton>
      </template>
      <template #footerButtonRight>
        <GbButton
          type="button"
          gbTheme="redPrimary"
          label="Delete Statement"
          @click="deleteStatement">
        </GbButton>
      </template>
    </GbDialog>

    <GbDialog
      dialog-id="clear-statement-modal"
      :visible="clearModalType !== null"
      @update:visible="(val) => { if (!val) clearModalType = null }"
      :header="clearModalConfig.header"
      :footer-buttons="true"
    >
      <template #bodyContent>
        <p v-html="clearModalConfig.body"></p>
      </template>
      <template #footerButtonLeft>
        <GbButton
          type="button"
          gbTheme="grayOutlineSecondary"
          label="Cancel"
          @click="clearModalType = null">
        </GbButton>
      </template>
      <template #footerButtonRight>
        <GbButton
          type="button"
          gbTheme="redPrimary"
          label="Clear Statement"
          @click="confirmClear">
        </GbButton>
      </template>
    </GbDialog>

    <PresentlevelsSkillsModal
      :key="selectedCategory?.uuid"
      :summary-uuid="props.summary?.uuid || ''"
      :category-label="selectedCategory?.label"
      :category-uuid="selectedCategory?.uuid || ''"
      :subject-label="props.summary?.subject_label || ''"
      :skill-level-groups="selectedCategory?.level_groups"
      @modal-closed="setSelectedCategory(null)"
      :disable-autosave="props.savedSummary"
      :statement-text="statementText"
      :aligned-goals="props.summary?.aligned_goals"
      :browse-assessment-domain="selectedCategory?.browse_assessment_domain || ''"
      :browse-assessment-subject="selectedCategory?.browse_assessment_subject || ''"
      :browse-assessment-type="selectedCategory?.browse_assessment_type || ''"
      :is-library="props.savedSummary"
    />
    <AssessmentUploadModal
      v-if="selectedReportMetadata"
      :visible="showAssessmentModal"
      :report-metadata="selectedReportMetadata"
      :subject="props.summary?.subject_label"
      :summary-u-u-i-d="props.summary?.uuid || ''"
      :subject-label="props.summary?.subject_label || ''"
      :grade-label="props.summary?.grade_label || ''"
      :grade-u-u-i-d="props.summary?.grade_uuid || ''"
      :grade-order="gradeOrder"
      @update:visible="showAssessmentModal = $event"
      @statement-inserted="handleStatementInserted"
      @aligned-goals-fetched="handleAlignedGoalsFetched"
    />
    <ContentReviewModal
      v-model:open="showReviewModal"
      :criteria-by-type="reviewCriteriaByType"
      :criteria-set-name="reviewCriteriaSetName"
      :subjects="props.summary?.subject_label ? [props.summary.subject_label] : []"
      content-label="Present Levels"
      description-label="present levels statement"
      @review="handleReview"
    />
    <slot />
  </div>
  <CopySummaryModal
    v-if="showCopySummaryModal"
    :content-uuid="summary?.uuid"
    :preserve-session="true"
    :post-copy-navigate="false"
    @save="() => $emit('copied')"
    @update:visible="(isVisible: boolean) => showCopySummaryModal = isVisible"
  />
</template>

<style>
/* Remove the tiptap's own bottom border and rounded corners for the Reading editor,
   so the Upload Score Report footer connects seamlessly to the text area */
.reading-plw-editor .tiptap {
  border-bottom-color: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
</style>